import React, { useEffect, useState, useCallback } from "react";
import { Menu, Divider, Box, Typography, Button, MenuItem, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedTestCase } from "../../Redux/TestAuthoring/TestAuthoring";
import {
  useCopyTCQuery,
  useDeleteTCQuery,
  useDownloadPomFilesQuery,
  useDownloadTestStepQuery,
  useRenameTCQuery,
  useTestSuiteQueryNew,
  useMoveProductsQuery,
} from "../../useQuery";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import SchemaIcon from "@mui/icons-material/Schema";

import useAddToSuiteMutation from "../../useQuery/useAddToSuiteMutation";
import { useNotification } from "../../hooks";
import {
  PlusIcon,
  CopyIcon,
  DeleteIcon,
  DownloadIcon,
  InfoIcon,
  NlpIcon,
  RenameIcon,
  ScreenshotIcon,
  PomDownloadIcon,
  InlineReplaceIcon,
  DownloadStepsIcon,
  DeleteStepsIcon,
  ChangeToAssertIcon,
  TimeIcon,
} from "../../components-new/icons";
import { CustomInput, CustomSelect } from "../../components-new/common";
import TSMenuItem from "./TSMenuItem";
import CustomDialog from "../../components-new/common/CustomDialog";
import useNlpStepQuery from "../../useQuery/useNlpStepQuery";
import useDownloadCodeQuery from "../../useQuery/useDownloadCodeQuery";
import useDownloadFeatureFileQuery from "../../useQuery/useDownloadFeatureFileQuery";
import useDownloadStepDefinitionQuery from "../../useQuery/useDownloadStepDefinitionQuery";
import { Flower, TimerIcon, Code2, Search } from "lucide-react";
import { ChangeCircle, ChevronRight, SwapHoriz, Visibility } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import LoaderSpinner from "../../components/common/LoaderSpinner";

const TSMenu = ({
  anchorEl,
  handleClose,
  handleExecuteTest,
  setTestCaseSearchKeyword,
  handleInlineTestSteps,
  handleDeleteTestSteps,
  handleCreateTestCase,
  onTestCaseAdded,
  handleChangeToAssert,
  handleSetFlow,
  handleSetWaitTime,
  handleBulkEditKeywordAction,
  handleSetCondLogic,
  handleRemoveConditionalLogic,
  handleFindSimilarSteps,
  handleSwapSteps,
  handleViewHiddenDeletedSteps,
  isReadOnly = false,
  selectedStepsCount = 0,
  setshowDependent,
  setshowDependency,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const selectedTestCase = useSelector((state) => state.authoringSlice.selectedTestcaseItem);
  const isMobileTestCase = selectedTestCase?.case_Type === "MOBILE";
  const selectedProduct = useSelector((state) => state.authoringSlice.selectedProduct);
  const [newName, setNewName] = useState("");
  const [copyName, setCopyName] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [selectedSuite, setSelectedSuite] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const notify = useNotification();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [bulkActionsMenuAnchor, setBulkActionsMenuAnchor] = useState(null);
  const [imageAnalysisMenuAnchor, setImageAnalysisMenuAnchor] = useState(null);
  const [dependentTestCases, setDependentTestCases] = useState([]);
  const [inlinedTestCases, setInlinedTestCases] = useState([]);
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [dependencyMenuAnchor, setDependencyMenuAnchor] = useState(null);

  const { data: products } = useMoveProductsQuery();

  const { mutate: addToSuite, isSuccess: isAddToSuiteSuccess } = useAddToSuiteMutation({
    onSuccess: () => {
      notify("Test case added to suite successfully", "success");
      handleCloseDialog();
    },
    onError: () => {
      notify("Failed to add test case to suite", "error");
    },
  });

  const {
    mutate: deleteTestCase,
    isPending: isDeleting,
    isSuccess: isSuccessDelete,
  } = useDeleteTCQuery({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
      queryClient.invalidateQueries({ queryKey: ["searchTCSearch"] });
      queryClient.invalidateQueries({ queryKey: ["testCase"] });
      queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });

      dispatch(setSelectedTestCase(null));
      setTestCaseSearchKeyword("");
      notify("The item has been successfully deleted.", "success");
      handleCloseDialog();
    },
    onError: (error) => {
      handleCloseDialog();
      const backendError = error?.response?.data;

      // 1️⃣ show backend message
      notify(backendError?.message || "Test case cannot be deleted due to dependencies", "error");

      const normalize = (arr) => (Array.isArray(arr?.[0]) ? arr[0] : arr || []);

      // 2️⃣ Populate BOTH arrays for the modal
      const dependent = backendError?.dependentTestCases || [];
      const inlined = backendError?.inlinedTestCases || [];

      setDependentTestCases(dependent);
      setInlinedTestCases(inlined);

      if (dependent.length > 0 || inlined.length > 0) {
        setShowDependencyModal(true);
      }

      // ✅ IMPORTANT FIX:
      queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
    },
  });

  const {
    data: renameData,
    mutate: renameTestCase,
    isSuccess: isSuccessRename,
    reset,
  } = useRenameTCQuery();
  const { data: copyData, mutate: copyTestCase, isSuccess: isSuccessCopy } = useCopyTCQuery();

  const { data: testSuites, isLoading: isLoadingTestSuites } = useTestSuiteQueryNew(
    selectedProductId,
    !!selectedProductId,
  );

  const { data: nlpStepsData, refetch: refetchNlpSteps } = useNlpStepQuery(
    selectedTestCase.idtest_case,
  );
  const { refetch: refetchDownloadCode } = useDownloadCodeQuery(
    selectedTestCase.idtest_case,
    selectedTestCase.Test_Case,
    isMobileTestCase,
  );
  const { refetch: refetchDownloadFeatureFile } = useDownloadFeatureFileQuery(
    selectedTestCase.idtest_case,
    selectedTestCase.Test_Case,
  );
  const { refetch: refetchDownloadStepDefinitions } = useDownloadStepDefinitionQuery(
    selectedTestCase.idtest_case,
    selectedTestCase.Test_Case,
  );
  const { refetch: refetchDownloadTestSteps } = useDownloadTestStepQuery(
    selectedTestCase.idtest_case,
  );
  const { refetch: refetchDownloadPomTestCase } = useDownloadPomFilesQuery(
    selectedTestCase?.idtest_case,
    "gpcTesting",
    "tcid",
  );
  const { refetch: refetchDownloadPomProduct } = useDownloadPomFilesQuery(
    selectedProduct,
    "genAllPOMFiles",
    "prodid",
  );

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSuite("");
    setSelectedProductId("");
    handleClose();
    setshowDependent(false);
    setshowDependency(false);
  };

  const handleDependencyMenuOpen = (event) => {
    closeAllSubmenus();
    setDependencyMenuAnchor(event.currentTarget);
  };

  const handleDependencyMenuClose = () => {
    setDependencyMenuAnchor(null);
  };

  const handleAddToTestSuite = () => {
    handleClose();
    setDialogType("addToSuite");
    setOpenDialog(true);
  };

  const closeAllSubmenus = () => {
    setDownloadMenuAnchor(null);
    setBulkActionsMenuAnchor(null);
    setImageAnalysisMenuAnchor(null);
    setDependencyMenuAnchor(null);
  };

  useEffect(() => {
    if (!anchorEl) {
      closeAllSubmenus();
    }
  }, [anchorEl]);

  const handleDownloadMenuOpen = (event) => {
    closeAllSubmenus();
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleBulkActionsMenuOpen = (event) => {
    closeAllSubmenus();
    setBulkActionsMenuAnchor(event.currentTarget);
  };

  const handleBulkActionsMenuClose = () => {
    setBulkActionsMenuAnchor(null);
  };

  const handleImageAnalysisMenuOpen = (event) => {
    closeAllSubmenus();
    setImageAnalysisMenuAnchor(event.currentTarget);
  };

  const handleImageAnalysisMenuClose = () => {
    setImageAnalysisMenuAnchor(null);
  };

  const handleConfirmAddToSuite = useCallback(() => {
    if (!selectedSuite) {
      notify("Please select a test suite", "error");
      return;
    }
    const testCasePayload = [selectedTestCase];

    addToSuite({
      data: testCasePayload,
      params: {
        idtest_suite: selectedSuite,
      },
    });
  }, [selectedSuite, selectedTestCase, addToSuite, notify]);

  const handleDownloadCode = () => {
    handleClose();
    refetchDownloadCode();
  };

  const handleDownloadFeatureFile = () => {
    handleClose();
    refetchDownloadFeatureFile();
  };

  const handleDownloadStepDefinitions = () => {
    handleClose();
    refetchDownloadStepDefinitions();
  };

  const handleDownloadTestSteps = () => {
    handleClose();
    refetchDownloadTestSteps();
  };

  const handleDownloadPomTestCase = () => {
    handleDownloadMenuClose();
    refetchDownloadPomTestCase();
  };

  const handleDownloadPomProduct = () => {
    handleDownloadMenuClose();
    refetchDownloadPomProduct();
  };

  const handleCopy = () => {
    handleClose();
    setDialogType("copy");
    setCopyName(selectedTestCase.Test_Case);
    setOpenDialog(true);
  };

  const handleRename = () => {
    handleClose();
    setDialogType("rename");
    setNewName(selectedTestCase.Test_Case);
    setOpenDialog(true);
  };

  const handleNlpSteps = () => {
    handleClose();
    setDialogType("nlpSteps");
    refetchNlpSteps();
    setOpenDialog(true);
  };

  const handleNameChange = useCallback(
    (e) => {
      const { value } = e.target;
      const regex = /^[a-zA-Z0-9 _ -]*$/;

      if (dialogType === "rename") {
        if (value.length <= 250 && regex.test(value)) {
          setNewName(value);
        }
      }
      if (dialogType === "copy") setCopyName(value);
    },
    [dialogType],
  );
  const openDependencyDialog = (type) => {
    if (type === "Dependencies") {
      setshowDependency(true);
    }

    if (type === "Dependant") {
      setshowDependent(true);
    }
  };

  const handleConfirm = useCallback(() => {
    switch (dialogType) {
      case "delete":
        deleteTestCase({ id: selectedTestCase.idtest_case });
        // dispatch(setSelectedTestCase(null));
        break;
      case "rename":
        setOpenDialog(false);
        renameTestCase({ id: selectedTestCase.idtest_case, newName });
        break;
      case "copy":
        setOpenDialog(false);
        copyTestCase({ tcid: selectedTestCase.idtest_case, copyNewName: copyName });
        break;
      default:
        setOpenDialog(false);
        break;
    }
  }, [
    dialogType,
    deleteTestCase,
    renameTestCase,
    copyTestCase,
    selectedTestCase.idtest_case,
    newName,
    copyName,
    dispatch,
  ]);

  useEffect(() => {
    const isNameUnchanged =
      dialogType === "rename"
        ? newName === selectedTestCase.Test_Case
        : copyName === selectedTestCase.Test_Case;
    setIsSaveDisabled(isNameUnchanged);
  }, [newName, copyName, dialogType, selectedTestCase.Test_Case]);

  useEffect(() => {
    if (isSuccessRename) {
      dispatch(setSelectedTestCase(renameData.data.idtest_case));
      handleClose();
      notify("The item has been renamed successfully.", "success");
      reset();
    }
  }, [dispatch, isSuccessRename, renameData?.data?.idtest_case, handleClose, notify]);

  useEffect(() => {
    if (isSuccessCopy) {
      handleClose();
      setTestCaseSearchKeyword("");
      dispatch(setSelectedTestCase(copyData.data.idtest_case));
      notify(`Test case copied with new name: ${copyName} successfully.`, "success");
    }
  }, [
    isSuccessCopy,
    copyData?.data?.idtest_case,
    copyName,
    dispatch,
    handleClose,
    notify,
    setTestCaseSearchKeyword,
  ]);

  const renderDialogContent = () => {
    switch (dialogType) {
      case "addToSuite":
        const suiteOptions = Array.isArray(testSuites)
          ? testSuites.flat()?.map((suite) => ({
              label: suite.Test_Suite,
              value: suite.idtest_suite,
            }))
          : [];

        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection="row" columnGap={2}>
              <CustomSelect
                label="Products"
                options={(Array.isArray(products) ? products : []).map((prodItem) => ({
                  label: prodItem.productname,
                  value: prodItem.idproducts,
                }))}
                width="230px"
                background="#ffffff"
                value={selectedProductId || ""}
                onChange={(e) => {
                  setSelectedProductId(e.target.value);
                  setSelectedSuite("");
                }}
              />
              <CustomSelect
                label="Suite"
                options={suiteOptions}
                width="230px"
                background="#ffffff"
                value={selectedSuite || ""}
                onChange={(e) => setSelectedSuite(e.target.value)}
                disabled={!selectedProductId}
              />
            </Box>
          </Box>
        );

      case "findSimilarSteps":
        const similarStepsSuiteOptions = Array.isArray(testSuites)
          ? testSuites.flat()?.map((suite) => ({
              label: suite.Test_Suite,
              value: suite.idtest_suite,
            }))
          : [];

        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Select a test suite to search for similar steps within.
            </Typography>
            <Box display="flex" flexDirection="row" columnGap={2}>
              <CustomSelect
                label="Products"
                options={(Array.isArray(products) ? products : []).map((prodItem) => ({
                  label: prodItem.productname,
                  value: prodItem.idproducts,
                }))}
                width="230px"
                background="#ffffff"
                value={selectedProductId || ""}
                onChange={(e) => {
                  setSelectedProductId(e.target.value);
                  setSelectedSuite("");
                }}
              />
              <CustomSelect
                label="Suite"
                options={similarStepsSuiteOptions}
                width="230px"
                background="#ffffff"
                value={selectedSuite || ""}
                onChange={(e) => setSelectedSuite(e.target.value)}
                disabled={!selectedProductId}
              />
            </Box>
          </Box>
        );

      case "delete":
        return isDeleting ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            {/* <LoaderSpinner /> */}
          </Box>
        ) : (
          <Typography>This action cannot be undone.</Typography>
        );
      case "rename":
        return (
          <>
            <Typography>Testcase name</Typography>
            <CustomInput autoFocus={true} value={newName} onChange={handleNameChange} />
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                color: newName.length >= 250 ? "error.main" : "text.secondary",
                textAlign: "right",
                display: "block",
              }}
            >
              {newName.length} / 250 characters
            </Typography>
          </>
        );
      case "copy":
        return (
          <>
            <Typography>Testcase name</Typography>
            <CustomInput autoFocus={true} value={copyName} onChange={handleNameChange} />
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                color: copyName.length >= 250 ? "error.main" : "text.secondary",
                textAlign: "right",
                display: "block",
              }}
            >
              {copyName.length} / 250 characters
            </Typography>
          </>
        );
      case "info":
        return (
          <Box display="flex" flexDirection="column" rowGap={2}>
            {[
              { label: "Created date", value: selectedTestCase.Created_Date },
              { label: "Created by", value: selectedTestCase.Created_By },
              { label: "Baseline Date", value: selectedTestCase.baselinedate },
            ].map((item, index) => (
              <Box key={index} display="flex" flexDirection="row" columnGap={2}>
                <Typography variant="subtitle2" width="100px">
                  {item.label}
                </Typography>
                <Typography>{item.value}</Typography>
              </Box>
            ))}
          </Box>
        );
      case "nlpSteps":
        return (
          <CustomInput
            fullWidth
            labelinput="Step Details"
            multiline
            rows={4}
            value={nlpStepsData?.nlpsteps}
            isDisabled="true"
            InputProps={{ readOnly: true }}
          />
        );
      default:
        return null;
    }
  };

  const renderDialogActions = () => {
    if (dialogType === "addToSuite") {
      return (
        <>
          <Button onClick={handleCloseDialog} sx={{ color: "#ff5336" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddToSuite}
            variant="contained"
            sx={{ background: "#ff5336" }}
            disabled={!selectedSuite}
          >
            Save
          </Button>
        </>
      );
    }
    if (dialogType === "findSimilarSteps") {
      return (
        <>
          <Button onClick={handleCloseDialog} sx={{ color: "#ff5336" }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleFindSimilarSteps(selectedSuite);
              handleCloseDialog();
            }}
            variant="contained"
            sx={{ background: "#ff5336" }}
            disabled={!selectedSuite}
          >
            Find Similar Steps
          </Button>
        </>
      );
    }
    if (dialogType === "delete") {
      return (
        <>
          <Button onClick={handleCloseDialog} disabled={isDeleting}>
            No
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="warning" disabled={isDeleting}>
            Yes
          </Button>
        </>
      );
    }
    if (["rename", "copy"].includes(dialogType)) {
      return (
        <>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary" disabled={isSaveDisabled}>
            Save
          </Button>
        </>
      );
    }
    return (
      <Button onClick={handleCloseDialog} color="primary">
        Close
      </Button>
    );
  };

  const renderDependencyContent = () => (
    <>
      {dependentTestCases.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Dependent Test Cases
          </Typography>

          <Typography sx={{ mb: 2 }}>
            These test cases depend on this test case (cannot delete):
          </Typography>

          <List>
            {dependentTestCases.map((tc) => (
              <ListItem key={tc.idtest_case}>
                <ListItemText
                  primary={tc.test_case_name}
                  secondary={`ID: ${tc.idtest_case} | Product: ${tc?.product || "N/A"} | Module: ${tc?.module || "N/A"}`}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {inlinedTestCases.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Inlined Test Cases
          </Typography>

          <Typography sx={{ mb: 2 }}>
            This test case is inlined inside the following test cases:
          </Typography>

          <List>
            {inlinedTestCases.map((tc) => (
              <ListItem key={tc.idtest_case}>
                <ListItemText
                  primary={tc.test_case_name}
                  secondary={`ID: ${tc.idtest_case} | Product: ${tc?.product || "N/A"} | Module: ${tc?.module || "N/A"}`}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* If somehow both are empty (shouldn’t normally happen) */}
      {dependentTestCases.length === 0 && inlinedTestCases.length === 0 && (
        <Typography>No dependencies found.</Typography>
      )}
    </>
  );

  const renderDependencyActions = () => (
    <>
      <Button
        variant="contained"
        color="warning"
        onClick={() => {
          setShowDependencyModal(false);
          handleDependencyMenuClose();
          handleCloseDialog();
          closeAllSubmenus();
        }}
      >
        Close
      </Button>
    </>
  );

  return (
    <Menu
      id="customized-menu"
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <TSMenuItem
        icon={<RenameIcon />}
        text="Rename"
        onClick={handleRename}
        onMouseEnter={closeAllSubmenus}
        disabled={isReadOnly}
      />
      <TSMenuItem
        icon={<CopyIcon />}
        text="Copy"
        onClick={handleCopy}
        onMouseEnter={closeAllSubmenus}
        disabled={isReadOnly}
      />
      <TSMenuItem
        icon={<Search size={20} />}
        text="Find Similar Steps"
        disabled={isReadOnly || selectedStepsCount !== 1}
        tooltip={selectedStepsCount !== 1 ? "Select exactly 1 step to find similar steps" : ""}
        onClick={() => {
          handleClose();
          setDialogType("findSimilarSteps");
          setOpenDialog(true);
        }}
        onMouseEnter={closeAllSubmenus}
      />
      <TSMenuItem
        icon={<Code2 />}
        text="Set Conditional Logic"
        onMouseEnter={closeAllSubmenus}
        disabled={isReadOnly}
        onClick={() => {
          handleClose();
          handleSetCondLogic();
        }}
      />
      <TSMenuItem
        icon={<PlusIcon color="#242F3F" />}
        text="Add to test suite"
        onClick={handleAddToTestSuite}
        onMouseEnter={closeAllSubmenus}
        disabled={isReadOnly}
      />

      <TSMenuItem
        icon={<InfoIcon color="#242F3F" />}
        text="Info"
        onClick={() => handleOpenDialog("info")}
        onMouseEnter={closeAllSubmenus}
      />

      <TSMenuItem
        icon={<DeleteIcon />}
        text="Delete Test Case"
        onMouseEnter={closeAllSubmenus}
        disabled={isReadOnly}
        onClick={() => {
          handleClose();
          handleOpenDialog("delete");
        }}
      />

      <Divider />

      <>
        <TSMenuItem
          icon={<ChangeCircle />}
          text="Bulk Actions"
          onMouseEnter={
            !isReadOnly && selectedStepsCount > 0 ? handleBulkActionsMenuOpen : undefined
          }
          endIcon={<ChevronRight />}
          disabled={isReadOnly || selectedStepsCount === 0}
          tooltip="Select test steps to perform bulk actions"
          onDisabledHover={closeAllSubmenus}
        />
        <Menu
          anchorEl={bulkActionsMenuAnchor}
          open={Boolean(bulkActionsMenuAnchor)}
          onClose={handleBulkActionsMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          MenuListProps={{
            onMouseLeave: handleBulkActionsMenuClose,
          }}
          sx={{
            pointerEvents: "none",
            "& .MuiPaper-root": {
              pointerEvents: "auto",
            },
          }}
        >
          <TSMenuItem
            icon={<PlusIcon />}
            text="Create Test Case"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleCreateTestCase();
            }}
          />
          <TSMenuItem
            icon={<Flower />}
            text="Set Flow Status"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleSetFlow();
            }}
          />
          <TSMenuItem
            icon={<TimerIcon />}
            text="Edit Wait Time"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleSetWaitTime();
            }}
          />
          <TSMenuItem
            icon={<ChangeCircle />}
            text="Bulk Edit Keyword & Action"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleBulkEditKeywordAction();
            }}
          />
          <TSMenuItem
            icon={<Code2 />}
            text="Remove Conditional Logic"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleRemoveConditionalLogic();
            }}
          />
          <Divider />

          <TSMenuItem
            icon={<InlineReplaceIcon />}
            text="Inline Test Steps"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleInlineTestSteps();
            }}
          />
          <TSMenuItem
            icon={<DeleteStepsIcon />}
            text="Delete Test Steps"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleDeleteTestSteps();
            }}
          />
          <TSMenuItem
            icon={<ChangeToAssertIcon />}
            text="Convert Steps to Assertions"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleChangeToAssert();
            }}
          />
          <TSMenuItem
            icon={<SwapHoriz />}
            text="Swap Steps"
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleSwapSteps();
            }}
          />
          <Divider />
          <TSMenuItem
            icon={<Visibility />}
            text="View Hidden & Deleted Steps"
            disabled={selectedStepsCount !== 2}
            tooltip={selectedStepsCount !== 2 ? "Select exactly 2 steps to define a range" : ""}
            onClick={() => {
              handleBulkActionsMenuClose();
              handleClose();
              handleViewHiddenDeletedSteps();
            }}
          />
        </Menu>
      </>

      {/* <TSMenuItem icon={<NlpIcon />} text="NLP steps" onClick={handleNlpSteps} /> */}

      <TSMenuItem
        icon={<ScreenshotIcon />}
        text="Image Analysis"
        onMouseEnter={isReadOnly ? undefined : handleImageAnalysisMenuOpen}
        endIcon={<ChevronRight />}
        disabled={isReadOnly}
        onDisabledHover={closeAllSubmenus}
      />
      <Menu
        anchorEl={imageAnalysisMenuAnchor}
        open={Boolean(imageAnalysisMenuAnchor)}
        onClose={handleImageAnalysisMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          onMouseLeave: handleImageAnalysisMenuClose,
        }}
        sx={{
          pointerEvents: "none",
          "& .MuiPaper-root": {
            pointerEvents: "auto",
          },
        }}
      >
        <TSMenuItem
          icon={<ScreenshotIcon />}
          text="Baseline Comparison"
          onClick={() => {
            handleImageAnalysisMenuClose();
            handleClose();
            handleExecuteTest(true);
          }}
        />
        <TSMenuItem
          icon={<ScreenshotIcon />}
          text="Visual Analysis"
          onClick={() => {
            handleImageAnalysisMenuClose();
            handleClose();
            handleExecuteTest(false, "analysis");
          }}
        />
      </Menu>
      <TSMenuItem
        icon={<SchemaIcon />}
        text="Dependencies"
        onMouseEnter={handleDependencyMenuOpen}
        endIcon={<ChevronRight />}
      />

      <Menu
        anchorEl={dependencyMenuAnchor}
        open={Boolean(dependencyMenuAnchor)}
        onClose={handleDependencyMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          onMouseLeave: handleDependencyMenuClose,
        }}
        sx={{
          pointerEvents: "none",
          "& .MuiPaper-root": {
            pointerEvents: "auto",
          },
        }}
      >
        <TSMenuItem
          icon={<SchemaIcon fontSize="small" />}
          text="Dependencies"
          onClick={() => {
            handleDependencyMenuClose();
            handleClose();
            openDependencyDialog("Dependencies");
          }}
        />

        <TSMenuItem
          icon={<AccountTreeIcon />}
          text="Dependant"
          onClick={() => {
            handleDependencyMenuClose();
            handleClose();
            openDependencyDialog("Dependant");
          }}
        />
      </Menu>

      <TSMenuItem
        icon={<DownloadIcon />}
        text="Downloads"
        onMouseEnter={isReadOnly ? undefined : handleDownloadMenuOpen}
        endIcon={<ChevronRight />}
        disabled={isReadOnly}
        onDisabledHover={closeAllSubmenus}
      />
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          onMouseLeave: handleDownloadMenuClose,
        }}
        sx={{
          pointerEvents: "none",
          "& .MuiPaper-root": {
            pointerEvents: "auto",
          },
        }}
      >
        {!isMobileTestCase && (
          <TSMenuItem
            icon={<PomDownloadIcon />}
            text="Download pom for selected test case"
            onClick={() => {
              handleDownloadMenuClose();
              handleClose();
              handleDownloadPomTestCase();
            }}
          />
        )}
          <TSMenuItem
            icon={<DownloadIcon />}
            text="Download Code"
            onClick={() => {
              handleDownloadMenuClose();
              handleDownloadCode();
            }}
          />
      </Menu>

      <CustomDialog
        open={openDialog}
        width={530}
        onClose={handleCloseDialog}
        title={
          dialogType === "delete"
            ? `Are you sure you want to delete ${selectedTestCase?.Test_Case}?`
            : dialogType === "rename"
              ? "Rename Testcase"
              : dialogType === "nlpSteps"
                ? "Test Case Step Details"
                : dialogType === "copy"
                  ? "Copy Testcase"
                  : dialogType === "addToSuite"
                    ? "Add to Test Suite"
                    : dialogType === "findSimilarSteps"
                      ? "Select Test Suite for Similar Steps"
                      : "Test Case Info"
        }
        content={renderDialogContent()}
        actions={renderDialogActions()}
      />

      <CustomDialog
        open={showDependencyModal}
        width={530}
        onClose={() => setShowDependencyModal(false)}
        title="Cannot delete test case"
        content={renderDependencyContent()}
        actions={renderDependencyActions()}
      />
    </Menu>
  );
};

export default TSMenu;
